import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Camera, Mail, Lock, Sparkles } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password, twoFactorCode || null);
      
      if (result.requires_two_factor) {
        setRequiresTwoFactor(true);
        toast.info("Ingresa el código de tu aplicación autenticadora");
      } else {
        toast.success("¡Bienvenido!");
        navigate("/");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Error al iniciar sesión";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
              <Camera className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <span className="font-heading font-bold text-primary">Fotomatón Costa Brava</span>
            </div>
            <h1 className="font-heading text-4xl font-bold text-primary">
              Bienvenido
            </h1>
            <p className="text-muted-foreground">
              Inicia sesión para gestionar tu negocio
            </p>
          </div>

          <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-xl font-heading">Iniciar sesión</CardTitle>
              <CardDescription>
                Introduce tus credenciales
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 rounded-xl border-border/60 focus:border-primary"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-12 rounded-xl border-border/60 focus:border-primary"
                      required
                    />
                  </div>
                </div>

                {requiresTwoFactor && (
                  <div className="space-y-2">
                    <Label htmlFor="twoFactor">Código de 2FA</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      <Input
                        id="twoFactor"
                        type="text"
                        placeholder="000000"
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value)}
                        className="pl-10 h-12 rounded-xl border-border/60 focus:border-primary text-center text-lg tracking-widest"
                        maxLength={6}
                        required
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Ingresa el código de 6 dígitos de tu aplicación autenticadora
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="login-submit-btn"
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-semibold shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin">◌</span>
                      Entrando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Iniciar sesión
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1561908669-04ac3e3cc0b2?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary/40 backdrop-blur-[2px]" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="backdrop-blur-xl bg-white/20 border border-white/30 rounded-3xl p-8 max-w-lg text-center shadow-2xl">
            <Camera className="w-16 h-16 text-white mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Gestiona tu negocio de fotomatones
            </h2>
            <p className="text-white/90 text-lg">
              Controla consultas, convierte clientes y organiza tus eventos desde un solo lugar.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
